import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';

import '../Auth.css';
import './SignUp.css';

const INDUSTRY_OPTIONS = [
  'Real Estate Investor',
  'Real Estate Broker',
  'Home Improvement',
  'Insurance',
  'Other'
];

const SignUp = ({ onLogin }) => {


  const climate_expertise_options = [
    {
      id: 'climate_beginner',
      label: 'Beginner',
      icon: "signup-climate-beginner-icon.svg"
    },
    {
      id: 'climate_intermediate',
      label: 'Intermediate',
      icon: "signup-climate-intermediate-icon.svg"
    },
    {
      id: 'climate_expert',
      label: 'Expert',
      icon: "signup-climate-expert-icon.svg"
    }
  ]

  const [stage, setStage] = useState('initial'); // 'initial', 'emailForm', 'companyInfo'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState('');
  const [signupMethod, setSignupMethod] = useState('');
  const [climateExpertise, setClimateExpertise] = useState(climate_expertise_options[0].id);
  const [areAllInputsValid, setAreAllInputsValid] = useState(false);

  // STATES TO MONITOR IF INPUTS ARE VALID
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isFirstNameValid, setIsFirstNameValid] = useState(false);
  const [isLastNameValid, setIsLastNameValid] = useState(false);

  const [isPasswordMoreThan8Chars, setIsPasswordMoreThan8Chars] = useState(false);
  const [isPasswordHasUppercase, setIsPasswordHasUppercase] = useState(false);
  const [isPasswordHasLowercase, setIsPasswordHasLowercase] = useState(false);
  const [isPasswordHasNumber, setIsPasswordHasNumber] = useState(false);
  const [isPasswordHasSpecialChar, setIsPasswordHasSpecialChar] = useState(false);

  // REGEX VALIDATIONS 

  useEffect(() => {
    // EMAIL VALIDATION
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email));
  }, [email]);

  useEffect(() => {
    // PASSWORD VALIDATION CHECK EACH ASPECT INDIVIDUALLY
    // MORE THAN 8 CHARS
    setIsPasswordMoreThan8Chars(password.length > 8);
    // HAS UPPERCASE
    setIsPasswordHasUppercase(password.match(/[A-Z]/));
    // HAS LOWERCASE
    setIsPasswordHasLowercase(password.match(/[a-z]/));
    // HAS NUMBER
    setIsPasswordHasNumber(password.match(/\d/));
    // HAS SPECIAL CHAR
    setIsPasswordHasSpecialChar(password.match(/[!@#$%^&*(),.?":{}|<]/));
  }, [password]);

  useEffect(() => {
    // PASSWORD VALIDATION CHECK IF ALL ASPECTS ARE VALID
    setIsPasswordValid(isPasswordMoreThan8Chars && isPasswordHasUppercase && isPasswordHasLowercase && isPasswordHasNumber && isPasswordHasSpecialChar);
  }, [isPasswordMoreThan8Chars, isPasswordHasUppercase, isPasswordHasLowercase, isPasswordHasNumber, isPasswordHasSpecialChar]);

  useEffect(() => {
    // FIRST NAME VALIDATION HAS LENGTH AND NO NUMBERS / SPECIAL CHARS
    setIsFirstNameValid(firstName.length > 0 && !/\d/.test(firstName));
    // LAST NAME VALIDATION HAS LENGTH AND NO NUMBERS / SPECIAL CHARS
    setIsLastNameValid(lastName.length > 0 && !/\d/.test(lastName));
  }, [firstName, lastName]);

  useEffect(() => {
    // CHECK IF ALL INPUTS ARE VALID
    setAreAllInputsValid(isEmailValid && isPasswordValid && isFirstNameValid && isLastNameValid);
  }, [isEmailValid, isPasswordValid, isFirstNameValid, isLastNameValid]);


  const handleGoogleSuccess = async (credentialResponse) => {
    const decodedToken = jwtDecode(credentialResponse.credential);
    setEmail(decodedToken.email);
    setFirstName(decodedToken.given_name);
    setLastName(decodedToken.family_name);
    setSignupMethod('sso');
    setStage('climateExpertise');
  };

  const handleEmailSignup = (e) => {
    e.preventDefault();
    if (areAllInputsValid) {
      setSignupMethod('form');
      setStage('climateExpertise');
    } else {
      setError('Please fill in all fields correctly.');
    }
  };

  const handleBackButton = () => {
    setStage('initial');
  };

  const handleBacktoEmailForm = () => {
    setStage('emailForm');
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    const userData = {
      email,
      password: signupMethod === 'sso' ? null : password,
      signup_method: signupMethod,
      sso_provider: signupMethod === 'sso' ? 'google' : null,
      climate_knowledge_level: climateExpertise,
      first_name: firstName,
      last_name: lastName
    };

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await axios.post(
        `${apiBaseUrl}/create-get-quoll-user`,
        userData
      );

      console.log('API Response:', response.data);

      if (response.data.status === 'pass') {
        console.log('Signup successful. User ID:', response.data.user_id);
        onLogin(response.data, 'signup'); // Pass the user data to the App component
      } else {
        console.log('Signup failed. Reason:', response.data.reason);
        setError('User already exists');
        setStage('initial');
      }
    } catch (error) {
      console.error('Signup error:', error);
      console.error('Error response:', error.response?.data);
      setError('An error occurred. Please try again.');
    }
  };

  const renderInitialStage = () => (
    <div className="auth-options">
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={() => setError('Google Sign-Up failed. Please try again.')}
        useOneTap
        type="standard"
        theme="filled_blue"
        size="large"
        text="signup_with"
        shape="pill"
        logo_alignment="left"
      />
      <div className="or-divider">
        <span>or</span>
      </div>
      <div className="button-group">
        <button onClick={() => setStage('emailForm')} className="email-signin-button">
          Use Email Address
        </button>
      </div>
    </div>
  );

  const passwordCheckDisplay = [
    {
      value: isPasswordMoreThan8Chars,
      label: '8+ characters',
    },
    {
      value: isPasswordHasUppercase,
      label: 'At least one uppercase letter',
    },
    {
      value: isPasswordHasLowercase,
      label: 'At least one lowercase letter',
    },
    {
      value: isPasswordHasNumber,
      label: 'At least one number',
    },
    {
      value: isPasswordHasSpecialChar,
      label: 'At least one special character',
    }
  ]

  const passwordCheckCheckmarkIconFilename = "password-validation-checkmark.svg";
  const passwordCheckCheckmarkIconUrl = CreateCloudfrontUrl(passwordCheckCheckmarkIconFilename);

  const renderEmailForm = () => (
    <form onSubmit={handleEmailSignup}>
      <div className="horizontal-inputs">
        <input
          className={`signup-input ${isFirstNameValid ? 'valid' : 'invalid'}`}
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
        <input
          className={`signup-input ${isLastNameValid ? 'valid' : 'invalid'}`}
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
        <input
          className={`signup-input ${isEmailValid ? 'valid' : 'invalid'}`}
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          className={`signup-input ${isPasswordValid ? 'valid' : 'invalid'}`}
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      {!isPasswordValid && (
        <div className="password-check-frame">
          <span className="password-check-title">Password Requirements</span>
          <div className="password-check-container">
            {passwordCheckDisplay.map((check) => (
              <div key={check.label} className="password-check-item">
                <div className={`password-check-icon-container ${check.value ? 'valid' : 'invalid'}`}>
                  <img src={passwordCheckCheckmarkIconUrl} alt="Password Checkmark" className={`password-check-icon ${check.value ? 'valid' : 'invalid'}`} />
                </div>
                <span className="password-check-label text-align-center">{check.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="button-group">
        <button type="button" onClick={handleBackButton} className="back-button">
          Back
        </button>
        <button className='auth-button-submit' type="submit">Next</button>
      </div>
    </form>
  );

  const renderCompanyInfoForm = () => (
    <form onSubmit={handleFinalSubmit}>
      <input
        type="text"
        placeholder="Company"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        required
      />
      <select
        value={industry}
        onChange={(e) => setIndustry(e.target.value)}
        required
      >
        <option value="">Select Industry</option>
        {INDUSTRY_OPTIONS.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
      <button type="submit">Sign Up</button>
    </form>
  );





  const renderClimateExpertiseForm = () => (
    <div className="climate-expertise-form">
      <span className="climate-expertise-form-title">How would you describe your climate risk knowledge?</span>
      <div className="climate-expertise-options-container">
        {climate_expertise_options.map((option) => (
          <div key={option.id} className={`climate-expertise-option ${climateExpertise === option.id ? 'active' : 'inactive'}`} onClick={() => setClimateExpertise(option.id)}>
            <div className="climate-expertise-option-icon-container">
              <img src={CreateCloudfrontUrl(option.icon)} alt={option.label} className="climate-expertise-option-icon" />
            </div>
            <div className="climate-expertise-option-label-container">
              <span>{option.label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="button-group">
        <button type="button" onClick={handleBacktoEmailForm} className="back-button">
          Back
        </button>
        <button
          type="submit"
          className="auth-button-submit"
          onClick={handleFinalSubmit}
        >
          Sign Up
        </button>
      </div>
    </div>
  );

  console.log('Current Stage:', stage);

  return (
    <>
      <div className="auth-content">
        {/* <h1 className="auth-header">Create Your Account</h1> */}
        {error && <p className="error-message">{error}</p>}
        {stage === 'initial' && renderInitialStage()}
        {stage === 'emailForm' && renderEmailForm()}
        {stage === 'companyInfo' && renderCompanyInfoForm()}
        {stage === 'climateExpertise' && renderClimateExpertiseForm()}
      </div>
    </>
  );
};

export default SignUp;
